/**
 * Статус водителя купона: полоса на кассе зеркалится в карточку ленты.
 *
 * Не сервер: остатки и купон человека на наш API не едут. Тот же мост, что miss.
 */

import type { DriveWord } from '../drive/ggbet';
import { Decimal } from 'decimal.js';
import { sameEventPage } from './event';
import { intentFits, intentOf } from './intent';
import { sameBook } from './pages';

export const DRIVE = 'forq:drive';
export type DriveFailure = 'event-missing' | 'market-missing' | 'page-unavailable';

export interface DriveNote {
  word: DriveWord;
  venue: string;
  url: string;
  at: number;
  odds?: string;
  amount?: string;
  detail?: string;
  failure?: DriveFailure;
  /** Купон конторы назвал максимальную сумму (валюта ноги). */
  maxStake?: string;
  /** Купон конторы: рынок приостановлен. */
  suspended?: boolean;
}

export function parseDrive(data: unknown): DriveNote | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== DRIVE) return null;
  const raw = body.drive;
  if (typeof raw !== 'object' || raw === null) return null;
  const one = raw as Record<string, unknown>;
  const word = one.word;
  if (
    word !== 'picked' &&
    word !== 'filled' &&
    word !== 'found' &&
    word !== 'drifted' &&
    word !== 'login' &&
    word !== 'miss'
  ) {
    return null;
  }
  if (typeof one.venue !== 'string' || one.venue === '') return null;
  if (typeof one.url !== 'string' || one.url === '') return null;
  const at = typeof one.at === 'number' && Number.isFinite(one.at) ? one.at : Date.now();
  const odds = typeof one.odds === 'string' && /^\d+(\.\d+)?$/.test(one.odds) ? one.odds : undefined;
  const amount = typeof one.amount === 'string' && /^\d+(\.\d+)?$/.test(one.amount) ? one.amount : undefined;
  const detail = typeof one.detail === 'string' && one.detail.trim() ? one.detail.trim().slice(0, 300) : undefined;
  const failure = ['event-missing', 'market-missing', 'page-unavailable'].includes(String(one.failure))
    ? one.failure as DriveFailure : undefined;
  const maxStake = typeof one.maxStake === 'string' && /^\d+(\.\d+)?$/.test(one.maxStake) ? one.maxStake : undefined;
  return { ...(detail ? { detail } : {}), ...(failure ? { failure } : {}), word, venue: one.venue, url: one.url, at,
    ...(odds === undefined ? {} : { odds }), ...(amount === undefined ? {} : { amount }),
    ...(maxStake === undefined ? {} : { maxStake }), ...(one.suspended === true ? { suspended: true } : {}) };
}

export const driveEnvelope = (drive: DriveNote): { channel: string; drive: DriveNote } => ({
  channel: DRIVE,
  drive,
});

export function driveText(word: DriveWord): string {
  if (word === 'picked') return 'исход выбран';
  if (word === 'filled') return 'купон готов';
  if (word === 'found') return 'событие открыто';
  if (word === 'drifted') return 'кэф или сумма уехали';
  if (word === 'login') return 'надо войти';
  return 'промах';
}

/** Полоса «исход выбран» висит на событии, не на площадку. Чужой слаг — молчим. */
export function driveFits(drive: DriveNote, pageUrl: string | null | undefined,
  expected?: { odds: string; amount: string }): boolean {
  if (pageUrl === null || pageUrl === undefined || pageUrl === '') return false;
  if (!intentFits(drive.url, pageUrl) || !sameBook(drive.url, pageUrl)) return false;
  if (expected !== undefined) {
    try {
      if (drive.odds === undefined || drive.amount === undefined
        || !new Decimal(drive.odds).eq(expected.odds) || !new Decimal(drive.amount).eq(expected.amount)) return false;
    } catch { return false; }
  }
  return intentOf(pageUrl) !== null || sameEventPage(drive.url, pageUrl);
}

/** Multiple cards and tabs on one venue must keep independent results. */
export function driveKey(venue: string, url: string): string {
  return JSON.stringify([venue, intentOf(url) ?? url]);
}
